import React, { useState } from "react";
import { fetchSystemLogs } from "../../api/systemLogService";
import { predictThreats } from "../../api/anomalyPredictionService";
import { processAiSecurityResult } from "../../api/adaptiveSecurityOrchestrationService";
import { updateDigitalTwinThreatPrediction } from "../../api/digitalTwinShipmentService";

const ThreatPredictionPanel = () => {
    const [threats, setThreats] = useState([]);
    const [securityResult, setSecurityResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const demoData = [
        { feature1: 90, feature2: 120, feature3: 1, timestamp: new Date().toISOString() },
        { feature1: 50, feature2: 80, feature3: 2, timestamp: new Date().toISOString() },
        { feature1: 10, feature2: 20, feature3: 3, timestamp: new Date().toISOString() },
    ];

    const buildFeaturesFromLogs = (logs) => {
        return logs.slice(0, 20).map((log, index) => {
            const level = (log.level || "").toLowerCase();
            const messageLength = log.message?.length || 0;

            return {
                feature1: level === "error" ? 100 : level === "warning" ? 60 : 10,
                feature2: messageLength,
                feature3: index,
                timestamp: log.timestamp || new Date().toISOString(),
            };
        });
    };

    const normalizeResult = (data) => {
        const raw = data?.data ?? data?.predictions ?? data;
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object") return [raw];
        return [];
    };

    const runThreatPrediction = async () => {
        setLoading(true);
        setError("");
        setThreats([]);
        setSecurityResult(null);
        const maxRiskScore = normalizedThreats.reduce((max, item) => {
            const value =
                item.anomalyScore !== undefined
                    ? Number(item.anomalyScore)
                    : item.score !== undefined
                        ? Number(item.score)
                        : 0;

            return value > max ? value : max;
        }, 0);

        const message = hasAnomaly
            ? "Threat detected by AI prediction"
            : "No critical threat detected";

        await updateDigitalTwinThreatPrediction(1, {
            hasThreat: hasAnomaly,
            riskScore: maxRiskScore,
            message,
        });
        try {
            let features = demoData;

            try {
                const logs = await fetchSystemLogs();
                if (Array.isArray(logs) && logs.length > 0) {
                    features = buildFeaturesFromLogs(logs);
                }
            } catch {
                features = demoData;
            }

            const data = await predictThreats(features);
            const normalizedThreats = normalizeResult(data);

            setThreats(normalizedThreats);

            const hasAnomaly = normalizedThreats.some(
                (x) => x.isAnomaly === true
            );

            const orchestrationResult = await processAiSecurityResult({
                shipmentId: 1,
                ipAddress: "127.0.0.1",
                hasAnomaly,
            });

            setSecurityResult(orchestrationResult);
        } catch (err) {
            setError(err?.message || "Failed to run threat prediction.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-2">Threat Prediction</h2>

            <p className="text-sm text-gray-600 mb-4">
                Predicts possible security threats based on system logs and anomaly patterns.
            </p>

            <button
                onClick={runThreatPrediction}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-60"
            >
                {loading ? "Running..." : "Run Threat Prediction"}
            </button>

            {error && (
                <div className="mt-4 p-3 rounded border border-red-300 bg-red-50 text-red-700">
                    {error}
                </div>
            )}

            {threats.length > 0 && (
                <div className="mt-4">
                    <h3 className="font-semibold mb-2">Threat Prediction Result</h3>

                    <table className="w-full text-sm border">
                        <thead>
                            <tr>
                                <th className="border p-2">Index</th>
                                <th className="border p-2">Threat Detected</th>
                                <th className="border p-2">Risk Score</th>
                                <th className="border p-2">Message</th>
                            </tr>
                        </thead>

                        <tbody>
                            {threats.map((item, index) => (
                                <tr key={item.index ?? index}>
                                    <td className="border p-2">{item.index ?? index}</td>
                                    <td className="border p-2">
                                        {item.isAnomaly ? "Yes" : "No"}
                                    </td>
                                    <td className="border p-2">
                                        {item.anomalyScore !== undefined
                                            ? Number(item.anomalyScore).toFixed(6)
                                            : item.score !== undefined
                                                ? Number(item.score).toFixed(6)
                                                : "—"}
                                    </td>
                                    <td className="border p-2">
                                        {item.message || "No additional information"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {securityResult && (
                <div className="mt-6 border rounded p-4 bg-gray-50">
                    <h3 className="font-bold mb-3">
                        Adaptive Security Decision
                    </h3>

                    <p><b>Trust Score:</b> {securityResult.trustScore?.score}</p>
                    <p><b>Risk Level:</b> {securityResult.trustScore?.riskLevel}</p>
                    <p><b>Allow Access:</b> {securityResult.decision?.allowAccess ? "Yes" : "No"}</p>
                    <p><b>Require 2FA:</b> {securityResult.decision?.requireTwoFactor ? "Yes" : "No"}</p>
                    <p><b>Block Operation:</b> {securityResult.decision?.blockOperation ? "Yes" : "No"}</p>
                    <p><b>Reason:</b> {securityResult.decision?.reason}</p>
                    <p><b>Blockchain Hash:</b> {securityResult.blockchainHash}</p>
                </div>
            )}
        </div>
    );
};

export default ThreatPredictionPanel;